import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { 
  SimulationProgress, 
  SimulationResults, 
  RideSchema, 
  FleetSettings,
  TimeWindowSettings as TimeWindowSettingsType,
  ProblemConfig,
  FleetPresetType
} from '../../types/simulation';
import { SimulationService } from '../../services/simulation/simulationService';
import { SimulationQueue, QueuedSimulation } from './SimulationQueue';
import { applyFleetPreset } from '../../services/simulation/syntheticData';
import FrodoConfigPanel from './FrodoConfigPanel';
import type { FrodoConfig } from '../../types/frodo';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
interface SimulationRunnerProps {
  rides: RideSchema[];
  fleet: FleetSettings | null;
  timeSettings: TimeWindowSettingsType | null;
  allSimulationResults: SimulationResults[];
  onSimulationResult: (result: SimulationResults) => void;
  selectedPresets?: string[];
  onPresetSelectionChange?: (selectedPresets: string[]) => void;
  selectedFleetPresets?: FleetPresetType[];
  onFleetPresetSelectionChange?: (selectedPresets: FleetPresetType[]) => void;
  onClearResults?: () => void;
}

export const SimulationRunner: React.FC<SimulationRunnerProps> = ({
  rides,
  fleet,
  timeSettings,
  allSimulationResults,
  onSimulationResult,
  selectedPresets = [],
  onPresetSelectionChange,
  selectedFleetPresets = [],
  onFleetPresetSelectionChange,
  onClearResults
}) => {
  const { t } = useTranslation('simulation');
  const [scenarioName, setScenarioName] = useState('My Simulation');
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<SimulationProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [simulationService] = useState(new SimulationService());
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [simulationQueue, setSimulationQueue] = useState<QueuedSimulation[]>([]);
  const [frodoConfig, setFrodoConfig] = useState<FrodoConfig>({
    enabled: false,
    selectedRouteIds: [],
    maxDistanceToStop: 500, // Default 500 meters
    excludeWheelchairUsers: true,
    maxWaitTimeMinutes: 10,
    allowTransfers: false,
  });

  // Auto-add selected preset combinations to queue when they change
  React.useEffect(() => {
    if (selectedPresets.length > 0 && selectedFleetPresets.length > 0 && rides.length > 0 && fleet && !isRunning) {
      // Generate all combinations of time and fleet presets
      const combinations: string[] = [];
      selectedPresets.forEach(timePreset => {
        selectedFleetPresets.forEach(fleetPreset => {
          combinations.push(`${timePreset}-${fleetPreset}`);
        });
      });
      
      const newSimulations: QueuedSimulation[] = combinations.map((combo, index) => {
        const [timePreset, fleetPreset] = combo.split('-');
        
        // Calculate the actual vehicle count after applying fleet preset
        const adjustedFleet = applyFleetPreset(fleet!, fleetPreset as FleetPresetType);
        const actualVehicleCount = adjustedFleet.vehicles.reduce((sum, v) => sum + v.number_of_vehicles, 0);
        
        return {
          id: `queue-${Date.now()}-${index}`,
          scenarioName: `${scenarioName}_${timePreset}-${fleetPreset}`,
          timePreset: timePreset as 'fast' | 'balanced' | 'thorough',
          fleetPreset: fleetPreset as 'auto' | 'savvy' | 'platinum',
          status: 'queued' as const,
          ridesCount: rides.length,
          vehiclesCount: actualVehicleCount
        };
      });

      setSimulationQueue(newSimulations);
    }
  }, [selectedPresets, selectedFleetPresets, rides.length, fleet, scenarioName, isRunning]);

  // Reset queue when preset selections change significantly
  React.useEffect(() => {
    if (selectedPresets.length === 0 || selectedFleetPresets.length === 0) {
      setSimulationQueue([]);
    }
  }, [selectedPresets.length, selectedFleetPresets.length]);

  // Queue management functions
  const removeSimulationFromQueue = (id: string) => {
    setSimulationQueue(prev => prev.filter(sim => sim.id !== id));
  };

  const cancelSimulation = (id: string) => {
    setSimulationQueue(prev => 
      prev.map(sim => 
        sim.id === id ? { ...sim, status: 'cancelled' as const } : sim
      )
    );
    setIsRunning(false);
    setProgress(null);
    setError(t('setup.runner.simulationCancelled'));
  };

  const runQueue = async () => {
    const queuedSimulations = simulationQueue.filter(sim => sim.status === 'queued');
    if (queuedSimulations.length === 0) return;
    
    setIsRunning(true);
    
    for (const queuedSim of queuedSimulations) {
      // Mark as running
      setSimulationQueue(prev => 
        prev.map(sim => 
          sim.id === queuedSim.id ? { ...sim, status: 'running' as const } : sim
        )
      );

      try {
        // Apply fleet preset
        const adjustedFleet = applyFleetPreset(fleet!, queuedSim.fleetPreset);
        
        // Create enhanced simulation config
        const config = simulationService.createEnhancedSimulationConfig(
          queuedSim.scenarioName,
          rides,
          adjustedFleet,
          {
            timeWindowSettings: timeSettings,
            optimizationLevel: queuedSim.timePreset
          }
        );

        // Run enhanced simulation
        const result = await simulationService.runEnhancedSimulation(config, (progress) => {
          setProgress(progress);
          setSimulationQueue(prev => 
            prev.map(sim => 
              sim.id === queuedSim.id ? { ...sim, progress: progress.progress } : sim
            )
          );
        });

        // Mark as completed
        setSimulationQueue(prev => 
          prev.map(sim => 
            sim.id === queuedSim.id ? { 
              ...sim, 
              status: 'completed' as const, 
              result: result,
              progress: 100
            } : sim
          )
        );

        // Add to results
        onSimulationResult(result);

      } catch (error: any) {
        // Mark as failed
        setSimulationQueue(prev => 
          prev.map(sim => 
            sim.id === queuedSim.id ? { 
              ...sim, 
              status: 'failed' as const, 
              error: error.message || t('setup.runner.simulationFailed')
            } : sim
          )
        );
      }
    }

    setIsRunning(false);
  };

  const handleRunSimulation = async () => {
    if (!rides.length || !fleet || !timeSettings) {
      setError(t('setup.runner.completeStepsError'));
      return;
    }

    setIsRunning(true);
    setError(null);
    setProgress(null);

    try {
      // Create enhanced simulation configuration
      const config = simulationService.createEnhancedSimulationConfig(
        scenarioName,
        rides,
        fleet,
        {
          timeWindowSettings: timeSettings,
          optimizationLevel: 'balanced'
        }
      );

      // Run enhanced simulation with progress tracking
      const result = await simulationService.runEnhancedSimulation(config, (progress) => {
        setProgress(progress);
      });

      onSimulationResult(result);
    } catch (err: any) {
      setError(err.message || t('setup.runner.simulationFailed'));
    } finally {
      setIsRunning(false);
    }
  };

  const handleStopSimulation = () => {
    setIsRunning(false);
    setProgress(null);
  };

  const getBestResult = (): SimulationResults | null => {
    if (allSimulationResults.length === 0) return null;
    
    return allSimulationResults.reduce((best, current) => {
      const bestScore = best.summary.assigned_rides / best.summary.total_rides;
      const currentScore = current.summary.assigned_rides / current.summary.total_rides;
      return currentScore > bestScore ? current : best;
    });
  };

  const bestResult = getBestResult();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Simulation Configuration */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('setup.runner.configurationTitle')}
          </Typography>

          <TextField
            fullWidth
            label={t('setup.runner.scenarioName')}
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            disabled={isRunning}
            sx={{ mb: 2 }}
          />

          {/* Advanced Settings Accordion */}
          <Accordion 
            expanded={showAdvancedSettings} 
            onChange={() => setShowAdvancedSettings(!showAdvancedSettings)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
              <Icon size={20} name="sliders" style={{ marginRight: 8 }} />
              <Typography variant="subtitle1">
                {t('setup.runner.advancedSettings')}
              </Typography>
              {timeSettings && (
                <Chip
                  label={t('setup.runner.customChip')}
                  size="small"
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info">
                <Typography variant="body2">
                  {t('setup.runner.advancedInfo', { window: timeSettings?.settings.window, maxTime: timeSettings?.settings.max_time_minutes })}
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          {/* FRODO Optimization Configuration */}
          <FrodoConfigPanel
            config={frodoConfig}
            onConfigChange={setFrodoConfig}
          />

          {/* Simulation Queue */}
          <SimulationQueue
            queue={simulationQueue}
            onRemoveSimulation={removeSimulationFromQueue}
            onCancelSimulation={cancelSimulation}
            onRunQueue={runQueue}
            onClearCompleted={() => setSimulationQueue(prev => prev.filter(sim => sim.status !== 'completed'))}
          />

          {!rides.length || !fleet || !timeSettings ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('setup.runner.completeStepsWarning')}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {rides.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.runner.rides')}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {fleet ? fleet.vehicles.reduce((sum, v) => sum + v.number_of_vehicles, 0) : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.runner.vehicles')}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {timeSettings?.settings.max_time_minutes || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.runner.maxTimeMin')}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {allSimulationResults.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.runner.results')}
                </Typography>
              </Paper>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Icon size={20} name="play" />}
              onClick={handleRunSimulation}
              disabled={isRunning || !rides.length || !fleet || !timeSettings}
            >
              {isRunning ? t('setup.runner.running') : t('setup.runner.run')}
            </Button>

            {isRunning && (
              <Button
                variant="outlined"
                startIcon={<Icon size={20} d={ICON_FALLBACKS.Stop} />}
                onClick={handleStopSimulation}
              >
                {t('setup.runner.stop')}
              </Button>
            )}

            {allSimulationResults.length > 0 && (
              <Button
                variant="text"
                startIcon={<Icon size={20} name="xmark" />}
                onClick={onClearResults}
              >
                {t('setup.runner.clearResults')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}

      {/* Best Result */}
      {bestResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('setup.runner.bestResult')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('setup.runner.scenario', { name: bestResult.scenario_name || t('setup.runner.unknownSimulation') })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="h4" color="success.main">
                  {bestResult.summary.assigned_rides}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.runner.assignedRides')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="h4" color="error.main">
                  {bestResult.summary.unassigned_rides}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.runner.unassignedRides')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="h4" color="primary.main">
                  {bestResult.summary.total_vehicles}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.runner.vehiclesUsed')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="h4" color="info.main">
                  {Math.round((bestResult.summary.assigned_rides / bestResult.summary.total_rides) * 100)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.runner.successRate')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* All Results Table */}
      {allSimulationResults.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                {t('setup.runner.allResultsTitle', { count: allSimulationResults.length })}
              </Typography>
              {onClearResults && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<Icon size={20} name="xmark" />}
                  onClick={onClearResults}
                  sx={{ textTransform: 'none' }}
                >
                  {t('setup.runner.clearResults')}
                </Button>
              )}
            </Box>
            
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={allSimulationResults.map((result, index) => ({
                  id: index,
                  results_id: result.results_id,
                  scenario_id: result.scenario_id,
                  scenario_name: result.scenario_name || `Simulation ${index + 1}`,
                  status: result.status,
                  assigned_rides: result.summary.assigned_rides,
                  unassigned_rides: result.summary.unassigned_rides,
                  total_rides: result.summary.total_rides,
                  total_vehicles: result.summary.total_vehicles,
                  assignment_rate: Math.round((result.summary.assigned_rides / result.summary.total_rides) * 100)
                }))}
                columns={[
                  {
                    field: 'results_id',
                    headerName: t('setup.runner.tableColumns.resultsId'),
                    width: 150,
                    flex: 1
                  },
                  {
                    field: 'scenario_name',
                    headerName: t('setup.runner.tableColumns.scenarioName'),
                    width: 250,
                    flex: 1.5
                  },
                  {
                    field: 'status',
                    headerName: t('setup.runner.tableColumns.status'),
                    width: 100,
                    renderCell: (params) => (
                      <Chip
                        label={params.value}
                        size="small"
                        color={params.value === 'completed' ? 'success' : 'error'}
                      />
                    )
                  },
                  {
                    field: 'assigned_rides',
                    headerName: t('setup.runner.tableColumns.assigned'),
                    width: 100,
                    type: 'number'
                  },
                  {
                    field: 'unassigned_rides',
                    headerName: t('setup.runner.tableColumns.unassigned'),
                    width: 100,
                    type: 'number'
                  },
                  {
                    field: 'total_rides',
                    headerName: t('setup.runner.tableColumns.totalRides'),
                    width: 100,
                    type: 'number'
                  },
                  {
                    field: 'assignment_rate',
                    headerName: t('setup.runner.tableColumns.assignmentRate'),
                    width: 130,
                    type: 'number',
                    renderCell: (params) => (
                      <Typography
                        variant="body2"
                        color={
                          params.value >= 90 ? 'success.main' :
                          params.value >= 70 ? 'warning.main' : 'error.main'
                        }
                        fontWeight="bold"
                      >
                        {params.value}%
                      </Typography>
                    )
                  },
                  {
                    field: 'total_vehicles',
                    headerName: t('setup.runner.tableColumns.vehicles'),
                    width: 100,
                    type: 'number'
                  }
                ]}
                pageSizeOptions={[5, 10, 25]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 }
                  },
                  sorting: {
                    sortModel: [{ field: 'assignment_rate', sort: 'desc' }]
                  }
                }}
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiDataGrid-columnHeader': {
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
